import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, RotateCcw, MessageSquare } from 'lucide-react';

const ReviewQuiz = ({ vocabulary, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  // Generate quiz questions from vocabulary
  const generateQuestions = () => {
    const questions = [];
    
    // Question 1: Translation (English to Native)
    const vocab1 = vocabulary[0];
    questions.push({
      type: 'multiple',
      question: `What does "${vocab1.word}" mean?`,
      options: [
        vocab1.translation,
        vocabulary[1]?.translation || 'Wrong answer',
        vocabulary[2]?.translation || 'Wrong answer',
        vocabulary[3]?.translation || 'Wrong answer'
      ].sort(() => Math.random() - 0.5),
      correct: vocab1.translation
    });

    // Question 2: Usage (Fill in blank)
    const vocab2 = vocabulary[1];
    const sentence = vocab2.usage.replace(vocab2.word.toLowerCase(), '____');
    questions.push({
      type: 'multiple',
      question: `Complete the sentence: "${sentence}"`,
      options: [
        vocab2.word,
        vocabulary[0]?.word || 'Wrong',
        vocabulary[2]?.word || 'Wrong',
        vocabulary[3]?.word || 'Wrong'
      ].sort(() => Math.random() - 0.5),
      correct: vocab2.word
    });

    // Question 3: Translation (Native to English)
    const vocab3 = vocabulary[2];
    questions.push({
      type: 'multiple',
      question: `What is "${vocab3.translation}" in English?`,
      options: [
        vocab3.word,
        vocabulary[0]?.word || 'Wrong',
        vocabulary[1]?.word || 'Wrong',
        vocabulary[4]?.word || 'Wrong'
      ].sort(() => Math.random() - 0.5),
      correct: vocab3.word
    });

    // Question 4: Context usage
    const vocab4 = vocabulary[3];
    questions.push({
      type: 'multiple',
      question: `When would you say "${vocab4.usage}"?`,
      options: [
        'Correct context',
        'At a bank',
        'At the gym',
        'During a meeting'
      ],
      correct: 'Correct context'
    });

    // Question 5: Matching
    const vocab5 = vocabulary[4];
    questions.push({
      type: 'multiple',
      question: `Which word means "${vocab5.translation}"?`,
      options: [
        vocab5.word,
        vocabulary[0]?.word || 'Wrong',
        vocabulary[1]?.word || 'Wrong',
        vocabulary[2]?.word || 'Wrong'
      ].sort(() => Math.random() - 0.5),
      correct: vocab5.word
    });

    return questions.slice(0, 5);
  };

  const [questions] = useState(generateQuestions());

  const handleAnswer = (answer) => {
    setSelectedAnswer(answer);
    setTimeout(() => {
      const isCorrect = answer === questions[currentQuestion].correct;
      setAnswers([...answers, isCorrect]);
      
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
      } else {
        setShowResults(true);
      }
    }, 500);
  };

  const retryQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
    setSelectedAnswer(null);
  };

  const correctCount = answers.filter(a => a).length;

  if (showResults) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-8 shadow-soft text-center"
        data-testid="quiz-results"
      >
        <div className="mb-6">
          {correctCount >= 4 ? (
            <CheckCircle className="w-16 h-16 text-lime mx-auto mb-4" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-clay/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📚</span>
            </div>
          )}
          <h2 className="font-fraunces text-3xl font-semibold text-forest mb-2">
            Quiz Complete!
          </h2>
          <p className="text-2xl text-forest font-medium">
            You got <span className="text-lime">{correctCount}</span> out of {questions.length} correct
          </p>
        </div>

        <div className="flex gap-4 justify-center">
          <Button
            onClick={retryQuiz}
            data-testid="retry-quiz-btn"
            variant="outline"
            className="rounded-full px-6 py-3 border-2 border-forest/20 hover:border-forest/40"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Retry Quiz
          </Button>
          <Button
            onClick={onComplete}
            data-testid="continue-conversation-btn"
            className="bg-forest hover:bg-forest/90 text-sand rounded-full px-6 py-3"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Continue to Conversation
          </Button>
        </div>
      </motion.div>
    );
  }

  const question = questions[currentQuestion];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-8 shadow-soft"
      data-testid="quiz-question"
    >
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-stone">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <div className="flex gap-2">
            {questions.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${
                  i < currentQuestion
                    ? answers[i]
                      ? 'bg-lime'
                      : 'bg-clay'
                    : i === currentQuestion
                    ? 'bg-forest'
                    : 'bg-stone/30'
                }`}
              />
            ))}
          </div>
        </div>
        <h3 className="font-fraunces text-2xl font-semibold text-forest">
          {question.question}
        </h3>
      </div>

      <div className="space-y-3">
        <AnimatePresence mode="wait">
          {question.options.map((option, i) => {
            const isSelected = selectedAnswer === option;
            const isCorrect = option === question.correct;
            const showFeedback = selectedAnswer !== null;

            return (
              <motion.button
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => !selectedAnswer && handleAnswer(option)}
                disabled={selectedAnswer !== null}
                className={`w-full p-4 rounded-2xl text-left transition-all ${
                  showFeedback
                    ? isSelected
                      ? isCorrect
                        ? 'bg-lime/20 border-2 border-lime'
                        : 'bg-clay/20 border-2 border-clay'
                      : isCorrect
                      ? 'bg-lime/10 border-2 border-lime/50'
                      : 'bg-sand border border-forest/10'
                    : 'bg-sand border border-forest/10 hover:border-forest/30 hover:bg-white'
                }`}
                data-testid={`quiz-option-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-forest">{option}</span>
                  {showFeedback && isSelected && (
                    isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-lime" />
                    ) : (
                      <XCircle className="w-5 h-5 text-clay" />
                    )
                  )}
                </div>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default ReviewQuiz;
